import { queryClient } from '@/infra/http'
import { userContentDetailsService } from '@/data/services/content/user-content-details-service'
import { userEnrollmentContentInfoService } from '@/data/services/content/user-enrollment-content-info-service'
import { productContentService } from '@/data/services/product/product-content-service'
import { PRODUCT_AVAILABLE_INFO_QUERY_KEY } from '@/data/services/product/product-content-service-types'
import { productFitScoreService } from '@/data/services/product/product-fit-score-service'
import { PRODUCT_FIT_SCORE_QUERY_KEY } from '@/data/services/product/product-fit-score-service-types'
import { checkFlagEnabled, FeatureFlagName } from '@/infra/feature-flag'

/**
 * Data loader da rota de detalhes do conteúdo (titulares e dependentes).
 *
 * Executa as queries em paralelo antes de renderizar a página:
 *
 * 1. `userContentDetails` — dados do produto (nome, formato, instituição, etc.)
 * 2. `userEnrollmentContentInfo` — elegibilidade e restrições de matrícula do usuário
 * 3. `productAvailableInfo` — cobertura de plano e planos disponíveis para o produto
 * 4. `fitScore` — compatibilidade do perfil com o curso (gated pela feature flag
 *    ENABLE_FIT_SCORE; falha silenciosa pra não derrubar a página inteira)
 *
 * Os estados disponíveis (usado por `use-enrollment-wizard-flags` e `EnrollmentUnitSelect`)
 * são buscados diretamente por esses componentes ao montar. O wizard exibe um Skeleton
 * enquanto essa query carrega, evitando blink de conteúdo.
 *
 * `isDependentContext` vem explicitamente do wiring da rota — não pode ser inferido
 * via `window.location` aqui porque loaders rodam ANTES da navegação ser commitada
 * (em navegação SPA o pathname ainda é o da página anterior).
 */
export async function loadContentDetails(contentId: number, isDependentContext = false) {
  const [userContentDetails, userEnrollmentContentInfo, productAvailableInfo, fitScore] =
    await Promise.all([
      queryClient.fetchQuery({
        queryKey: ['user-content-details', contentId],
        queryFn: () => userContentDetailsService.fetchUserContentDetails(contentId),
        staleTime: 1000
      }),
      queryClient.fetchQuery({
        queryKey: ['user-enrollment-content-info', contentId],
        queryFn: () => userEnrollmentContentInfoService.fetchUserEnrollmentContentInfo(contentId),
        staleTime: 1000
      }),
      queryClient.fetchQuery({
        queryKey: [PRODUCT_AVAILABLE_INFO_QUERY_KEY, contentId],
        queryFn: () =>
          productContentService.fetchAvailableInfo({
            productId: contentId,
            isDependentContext
          }),
        staleTime: 0
      }),
      // Fit-score gated pela feature flag, com fallback silencioso pra não derrubar
      // a página inteira se a API falhar (feature é secundária ao core da PDV).
      checkFlagEnabled(FeatureFlagName.ENABLE_FIT_SCORE)
        ? queryClient
            .fetchQuery({
              queryKey: [PRODUCT_FIT_SCORE_QUERY_KEY, contentId],
              queryFn: () => productFitScoreService.fetchProductFitScore(contentId),
              staleTime: 1000
            })
            .catch(() => null)
        : null
    ])

  return {
    userContentDetails,
    userEnrollmentContentInfo,
    productAvailableInfo,
    fitScore
  }
}
